import Promise from 'bluebird';
import { useState, useCallback, useEffect, useContext } from 'react';
import { odata } from '@bingads-webui/http-util';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import _ from 'underscore';


const oDataParams = {
  contentType: 'application/json',
  path: 'AdInsight/V2/',
  customHeader: {
    name: 'x-ms-scenarioname',
    value: 'DisplayPlusCampaign',
  },
};

const FetchType = { total: 'total', included: 'included', all: 'all' };

const memoizedOdataPost = _.memoize(odataProps => odata.post(odataProps));

export const useIMATargetingEstimationCaller = (targetsForTotalAudienceSizeEstimation, targetsForIncludedAudienceSizeEstimation, targetsForFullTargetedAudienceSizeEstimation) => {
  const { scope } = useContext(ScopeContext) || {};
  const serverContext = useContext(ServerContext);
  const { cid, aid } = scope.values;
  const {
    ServicesEndpoints: { CampaignAPI: host },
    Token: token,
    Urls: { SignOutUrl = null } = {},
  } = serverContext;
  const urlBase = `Customers(${cid})/Accounts(${aid})/`;
  const audienceSizeEstimationUrl = `${urlBase}Default.AudienceEstimationV2`;

  const [isLoading, setIsLoading] = useState(false);
  const [totalPotentialData, setTotalPotentialData] = useState(null);
  const [includedOnlyData, setIncludedOnlyData] = useState(null);
  const [allTargetedData, setAllTargetedData] = useState(null);
  const [isImpressionTooSpecific, setIsImpressionTooSpecific] = useState(false);

  const getAudienceReachEstimation = useCallback((fetchType, targets) => {
    setIsLoading(true);

    return Promise.props({
      audienceSizeEstimation: memoizedOdataPost({
        url: audienceSizeEstimationUrl,
        SignOutUrl,
        host,
        oDataToken: token,
        data: { request: targets },
        ...oDataParams,
      }),
    })
      .then(({ audienceSizeEstimation }) => {
        if (audienceSizeEstimation) {
          switch (fetchType) {
            case FetchType.total:
              setTotalPotentialData(audienceSizeEstimation);
              break;
            case FetchType.included:
              setIncludedOnlyData(audienceSizeEstimation);
              break;
            case FetchType.all:
              setAllTargetedData(audienceSizeEstimation);
              break;
            default:
              break;
          }
          setIsImpressionTooSpecific(audienceSizeEstimation.IsImpressionTooSpecific);
        }
      })
      .catch(err => Promise.reject(err))
      .finally(() => {
        setIsLoading(false);
      });
  }, [audienceSizeEstimationUrl, setTotalPotentialData, setIncludedOnlyData, setAllTargetedData]);

  useEffect(() => {
    getAudienceReachEstimation(FetchType.total, targetsForTotalAudienceSizeEstimation);
  }, [targetsForTotalAudienceSizeEstimation, getAudienceReachEstimation]);

  useEffect(() => {
    getAudienceReachEstimation(FetchType.included, targetsForIncludedAudienceSizeEstimation);
  }, [getAudienceReachEstimation, targetsForIncludedAudienceSizeEstimation]);

  useEffect(() => {
    getAudienceReachEstimation(FetchType.all, targetsForFullTargetedAudienceSizeEstimation);
  }, [getAudienceReachEstimation, targetsForFullTargetedAudienceSizeEstimation]);

  return [isLoading, totalPotentialData, includedOnlyData, allTargetedData, isImpressionTooSpecific];
};
